
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { useLocation } from "react-router-dom";
import { useGlobalCache, createSupabaseSubscription } from "@/hooks/use-global-cache";

export const useClientInfo = () => {
  const [clientId, setClientId] = useState<string | null>(null);
  const [isClientView, setIsClientView] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clientSlug, setClientSlug] = useState<string | null>(null);
  const { user } = useAuth();
  const location = useLocation();

  // Utilizziamo useGlobalCache per memorizzare i dati dell'utente
  const { data: profileData, isLoading: isProfileLoading } = useGlobalCache(
    `profile_${user?.id || 'anonymous'}`,
    async () => {
      if (!user) return null;

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      
      // Se l'utente è un client, otteniamo anche i suoi dati
      if (profile.role === 'user') {
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('id, first_name, last_name')
          .eq('auth_user_id', user.id)
          .single();
          
        if (clientError) throw clientError;
        
        return { 
          profile, 
          clientData 
        };
      }
      
      return { profile };
    },
    {
      validity: 12 * 60 * 60 * 1000, // 12 ore (questi dati cambiano raramente)
      staleTime: 1 * 60 * 60 * 1000, // 1 ora di stale time
      setupRealtimeSubscription: (notifyUpdate) => {
        if (!user) return () => {};
        
        // Creiamo due sottoscrizioni: una per profiles e una per clients
        const profileSubscription = createSupabaseSubscription(
          supabase, 
          'profiles',
          { filter: `id=eq.${user.id}` },
          notifyUpdate
        );
        
        // Per i clients, possiamo sottoscriverci solo se sappiamo di essere un client
        const clientSubscription = createSupabaseSubscription(
          supabase,
          'clients',
          { filter: `auth_user_id=eq.${user.id}` },
          notifyUpdate
        );
        
        // Restituiamo una funzione che pulisce entrambe le sottoscrizioni
        return () => {
          profileSubscription();
          clientSubscription();
        };
      }
    }
  );

  // Aggiorniamo gli stati usando i dati della cache quando disponibili
  useEffect(() => {
    if (profileData) {
      const { profile, clientData } = profileData;
      
      if (profile.role === 'user') {
        setIsClientView(true);
        setIsAdmin(false);
        
        if (clientData) {
          setClientId(clientData.id);
          
          if (clientData.first_name && clientData.last_name) {
            const slug = `${clientData.first_name.toLowerCase()}-${clientData.last_name.toLowerCase()}`;
            setClientSlug(slug);
          }
        }
      } else {
        setIsClientView(false);
        setIsAdmin(profile.role === 'admin');
      }
    } else {
      // Reset degli stati se non ci sono dati
      setIsAdmin(false);
      setIsClientView(false);
      setClientId(null);
      setClientSlug(null);
    }
  }, [profileData]);

  return {
    clientId,
    clientSlug,
    isClientView,
    isAdmin,
    user,
    isLoading: isProfileLoading
  };
};
